import Quill from 'quill';

/** HTML permitido no texto rico do deck CAB (rótulo / texto livre). */

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'SPAN']);

const ALLOWED_STYLE_PROPS = /^(color|font-size|font-weight|font-style|text-decoration)$/i;

/** Limite defensivo contra colar HTML enorme / árvore patológica (navegador). */
export const CAB_DECK_HTML_MAX_CHARS = 8000;
const CAB_DECK_HTML_MAX_NODES = 800;
const SANITIZE_CACHE_MAX = 48;

let quillSizeRegistered = false;
const sanitizeCache = new Map<string, string>();

/** Tamanhos relativos ao tamanho-base do bloco (slider % do slide). */
export const CAB_DECK_SIZE_WHITELIST = ['0.85em', '1em', '1.25em', '1.6em', '2em'];

/** Registra size/color como style= (não classes ql-*), para o HTML sobreviver ao sanitize e ao canvas. */
export function registerCabDeckQuillSize(): void {
  if (quillSizeRegistered) return;
  try {
    const SizeStyle = Quill.import('attributors/style/size') as { whitelist?: string[] };
    SizeStyle.whitelist = CAB_DECK_SIZE_WHITELIST;
    Quill.register(SizeStyle, true);
    const ColorStyle = Quill.import('attributors/style/color');
    Quill.register(ColorStyle, true);
    quillSizeRegistered = true;
  } catch {
    /* ambiente de teste sem Quill completo */
  }
}

export function escapeCabDeckPlain(text: string): string {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function clampHtmlInput(raw: string): string {
  const value = String(raw || '');
  if (value.length <= CAB_DECK_HTML_MAX_CHARS) return value;
  return value.slice(0, CAB_DECK_HTML_MAX_CHARS);
}

function rememberSanitize(key: string, value: string): string {
  if (sanitizeCache.size >= SANITIZE_CACHE_MAX) {
    const oldest = sanitizeCache.keys().next().value;
    if (oldest !== undefined) sanitizeCache.delete(oldest);
  }
  sanitizeCache.set(key, value);
  return value;
}

function sanitizeStyle(value: string): string {
  return value
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf(':');
      if (idx < 0) return '';
      const prop = part.slice(0, idx).trim();
      const val = part.slice(idx + 1).trim();
      if (!ALLOWED_STYLE_PROPS.test(prop)) return '';
      if (val.length > 64) return '';
      if (/expression|url\s*\(|javascript:/i.test(val)) return '';
      return `${prop}: ${val}`;
    })
    .filter(Boolean)
    .join('; ');
}

function walkSanitize(node: Node, out: DocumentFragment | Element, budget: { n: number }): void {
  if (budget.n <= 0) return;
  budget.n -= 1;

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    if (text) out.appendChild(document.createTextNode(text));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  const tag = el.tagName.toUpperCase();
  if (!ALLOWED_TAGS.has(tag)) {
    Array.from(el.childNodes).forEach((child) => walkSanitize(child, out, budget));
    return;
  }
  const cleanTag = tag === 'B' ? 'strong' : tag === 'I' ? 'em' : tag.toLowerCase();
  const clean = document.createElement(cleanTag);
  if (tag === 'SPAN' || tag === 'P' || tag === 'STRONG' || tag === 'EM' || tag === 'U') {
    const styles: string[] = [];
    const style = el.getAttribute('style');
    if (style) {
      const safe = sanitizeStyle(style);
      if (safe) styles.push(safe);
    }
    /* HTML antigo do Quill com classes ql-color-* / ql-size-* → style inline. */
    const cls = el.getAttribute('class') || '';
    const colorClass = cls.match(/\bql-color-([^\s]+)\b/);
    if (colorClass && !/color\s*:/i.test(styles.join(';'))) {
      const raw = decodeURIComponent(colorClass[1]).replace(/^_+/, '#');
      if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw) || /^[a-z]{1,24}$/i.test(raw)) {
        styles.push(`color: ${raw}`);
      }
    }
    const sizeClass = cls.match(/\bql-size-([^\s]+)\b/);
    if (sizeClass && !/font-size\s*:/i.test(styles.join(';'))) {
      const map: Record<string, string> = {
        small: '0.85em',
        large: '1.25em',
        huge: '1.6em',
      };
      const sz = map[sizeClass[1]] || (CAB_DECK_SIZE_WHITELIST.includes(sizeClass[1]) ? sizeClass[1] : '');
      if (sz) styles.push(`font-size: ${sz}`);
    }
    if (styles.length) clean.setAttribute('style', styles.join('; '));
  }
  Array.from(el.childNodes).forEach((child) => walkSanitize(child, clean, budget));
  out.appendChild(clean);
}

/** Converte texto puro legado em HTML simples; HTML passa pelo sanitizador. */
export function normalizeCabDeckRichHtml(raw: string | null | undefined): string {
  const value = clampHtmlInput(String(raw || ''));
  if (!value.trim()) return '';
  if (!/<[a-z][\s\S]*>/i.test(value)) {
    return `<p>${escapeCabDeckPlain(value).replace(/\n/g, '<br/>')}</p>`;
  }
  return sanitizeCabDeckHtml(value);
}

export function sanitizeCabDeckHtml(raw: string): string {
  const input = clampHtmlInput(String(raw || ''));
  if (!input) return '';
  const hit = sanitizeCache.get(input);
  if (hit !== undefined) return hit;

  if (typeof document === 'undefined') {
    return rememberSanitize(input, escapeCabDeckPlain(input));
  }
  const parsed = new DOMParser().parseFromString(input, 'text/html');
  const frag = document.createDocumentFragment();
  const budget = { n: CAB_DECK_HTML_MAX_NODES };
  Array.from(parsed.body.childNodes).forEach((child) => walkSanitize(child, frag, budget));
  const wrap = document.createElement('div');
  wrap.appendChild(frag);
  return rememberSanitize(input, wrap.innerHTML);
}

/** Só para testes: esvazia o cache do sanitizador. */
export function clearCabDeckSanitizeCacheForTests(): void {
  sanitizeCache.clear();
}

export const CAB_DECK_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ size: CAB_DECK_SIZE_WHITELIST }],
    [{ color: [] }],
    ['clean'],
  ],
};

export const CAB_DECK_QUILL_FORMATS = ['bold', 'italic', 'underline', 'size', 'color'];
